import { useMutation } from '@apollo/client'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addHours, differenceInSeconds, isBefore, startOfDay } from 'date-fns'
import { SalaryWorkTimeNode } from 'modules/companies'
import {
  CREATE_TIME_ENTRY_MUTATION,
  DELETE_TIME_ENTRY_MUTATION,
  PATCH_TIME_ENTRY_MUTATION,
} from 'modules/timesheets/mutations'
import { useCallback, useMemo, useState } from 'react'
import { format } from 'utils/date-fns'
import { ERROR_TYPES, useConfirm, useOnErrorAuto } from 'utils/hooks'
import { CheckOutModal, TerminalEntryNode } from '.'
import { EditTerminalEntryForm } from './types'

export function useTerminalEntriesMutations(
  latestEntry: TerminalEntryNode | null = null,
  lastCheckIn: Date = new Date(),
  userId: string = '',
  salaryWorkTime: Pick<
    SalaryWorkTimeNode,
    'dailyWorkLimit' | 'pauseDuration'
  > | null = null
) {
  const translations = useTranslate({
    error: {
      checkIn: 'error.could-not-check-in',
      checkOut: 'error.could-not-check-out',
    },
    toasts: {
      checkedInAt: ['terminal.checked-in-at', { time: '' }],
      checkedOutAt: ['terminal.checked-out-at', { time: '' }],
      editEntrySuccess: 'terminal.success.edit-terminal-entry',
      editEntryError: 'terminal.success.edit-terminal-entry',
      deleteEntrySuccess: 'terminal.success.delete-terminal-entry',
      deleteEntryError: 'terminal.error.delete-terminal-entry',
    },
    prompt: {
      deleteTerminalEntry: 'terminal.prompt.delete-terminal-entry',
      deleteTerminalEntryTitle: 'terminal.prompt.delete-terminal-entry-title',
    },
  })

  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()
  const addPrompt = usePrompt()

  /* CheckInOutButton */

  const [createTimeEntry, { loading: createTimeEntryLoading }] = useMutation(
    CREATE_TIME_ENTRY_MUTATION,
    {
      refetchQueries: ['TodaysTerminalEntries', 'AllTerminalEntries'],
      awaitRefetchQueries: true,
      onCompleted: () => {
        addToast(
          'success',
          translations.toasts.checkedInAt({ time: format(new Date(), 'HH:mm') })
        )
      },
      onError: onErrorAuto(translations.error.checkIn, {
        logError: (_, key) => key !== ERROR_TYPES.OVERLAPPING_TIME_ENTRY,
        showToast: (_, key) => key !== ERROR_TYPES.OVERLAPPING_TIME_ENTRY,
      }),
    }
  )

  const [patchTimeEntry, { loading: patchTimeEntryLoading }] = useMutation(
    PATCH_TIME_ENTRY_MUTATION,
    {
      refetchQueries: ['TodaysTerminalEntries', 'AllTerminalEntries'],
      awaitRefetchQueries: true,
      onCompleted: () => {
        addToast(
          'success',
          translations.toasts.checkedOutAt({
            time: format(new Date(), 'HH:mm'),
          })
        )
      },
      onError: onErrorAuto(translations.error.checkOut),
    }
  )

  const [deleteTimeEntry, { loading: deleteTimeEntryLoading }] = useMutation(
    DELETE_TIME_ENTRY_MUTATION,
    {
      onCompleted: () => {
        addToast('success', translations.toasts.deleteEntrySuccess)
      },
      onError: () => {
        addToast('error', translations.toasts.deleteEntryError)
      },
    }
  )

  const forgotToCheckOut = useMemo(
    () =>
      latestEntry && lastCheckIn
        ? isBefore(lastCheckIn, startOfDay(new Date())) &&
          differenceInSeconds(new Date(), lastCheckIn) >
            (salaryWorkTime?.dailyWorkLimit ?? 0) -
              (salaryWorkTime?.pauseDuration ?? 0)
        : false,
    [
      latestEntry,
      lastCheckIn,
      salaryWorkTime?.dailyWorkLimit,
      salaryWorkTime?.pauseDuration,
    ]
  )

  const handleCheckIn = useCallback(async () => {
    await createTimeEntry({
      variables: {
        input: {
          user: userId,
          datetimeStart: new Date().toISOString(),
          fromTerminal: true,
        },
      },
    })
  }, [createTimeEntry, userId])

  const handleCheckOut = useCallback(async () => {
    if (!latestEntry || !lastCheckIn) return

    const { data } = await addPrompt<string | null>(resolve => (
      <CheckOutModal timeEntry={latestEntry} onResolve={resolve} />
    ))
    if (data === null) return

    const comment = data || null

    if (forgotToCheckOut) {
      /* TODO: replace with suitable SalaryWorkTimeModel value */
      const normalWorkday = 8 // hours
      const timeWorked = salaryWorkTime
        ? (salaryWorkTime.dailyWorkLimit ?? 0) / 3600
        : normalWorkday

      const datetimeEnd = addHours(lastCheckIn, timeWorked).toISOString()
      await patchTimeEntry({
        variables: {
          id: latestEntry.id,
          input: {
            user: userId,
            datetimeEnd,
            comment,
            allowOverride: true,
            fromTerminal: true,
          },
        },
      })
    } else {
      await patchTimeEntry({
        variables: {
          id: latestEntry.id,
          input: {
            user: userId,
            datetimeEnd: new Date().toISOString(),
            comment,
            allowOverride: true,
            fromTerminal: true,
          },
        },
      })
    }
  }, [
    latestEntry,
    lastCheckIn,
    addPrompt,
    forgotToCheckOut,
    salaryWorkTime,
    patchTimeEntry,
    userId,
  ])

  /* Edit Terminal Entry */

  const confirm = useConfirm()
  const [entryLoading, setEntryLoading] = useState<string | null>(null)
  const [editTerminalEntry, setEditTerminalEntry] =
    useState<TerminalEntryNode | null>(null)

  async function handleEditTerminalEntry(values: EditTerminalEntryForm) {
    const entry = editTerminalEntry
    if (!entry) return

    setEditTerminalEntry(null)

    try {
      setEntryLoading(entry.id)
      await patchTimeEntry({
        variables: {
          id: entry.id,
          input: {
            datetimeStart: values.checkIn.toISOString(),
            datetimeEnd: values.checkOut.toISOString(),
          },
        },
      })
    } finally {
      setEntryLoading(null)
    }
  }

  async function handleDeleteTerminalEntry(entry: TerminalEntryNode) {
    const promptMessage = translations.prompt.deleteTerminalEntry
    const promptTitle = translations.prompt.deleteTerminalEntryTitle

    const { data: answer } = await confirm(promptMessage, promptTitle)
    if (!answer) return

    try {
      setEntryLoading(entry.id)
      await deleteTimeEntry({
        variables: { id: entry.id },
      })
    } finally {
      setEntryLoading(null)
    }
  }

  const mutationLoading =
    createTimeEntryLoading || patchTimeEntryLoading || deleteTimeEntryLoading

  return {
    handleCheckIn,
    handleCheckOut,
    loading: mutationLoading,

    editTerminalEntry,
    entryLoading,
    setEditTerminalEntry,
    handleEditTerminalEntry,
    handleDeleteTerminalEntry,
  }
}
